import React, { useState, useEffect } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { verifyDuplicatePesel, verifyDuplicateMail } from '../../services/validation/validation.service';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';
import { Grid, TextField } from '@material-ui/core';
import AddStageInfo from './auxiliary/AddStageInfo1.component';
import {
  validateFormatEmailInput,
  validatePhoneNumberInput,
  validatateAreaPolishPhoneCode,
} from '../../services/validation/validationUser';
import { isPeselValid } from '../../utils/peselValidator';
import { useStyles, styles, CancelButton, NextButton } from './AddPersonalData.styles';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { useLayoutContext } from '../../Context/Layout.context';
import CloseButton from '../buttons/CloseButtton.component';
import { FormControlLabel } from '@mui/material';
import { CheckboxOne } from '../CheckboxOne/CheckboxOne.component';

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      {children}
    </MuiDialogTitle>
  );
});

interface IPersonalDataProps {
  nameArg: string;
  surnameArg: string;
  peselArg: string;
  phoneArg: string;
  phoneHiddenArg: boolean;
  mailArg: string;
  closeArg: boolean;
  toStage2: boolean;
  personalDataToParent: (
    name: string,
    surname: string,
    pesel: string,
    phone: string,
    phoneHidden: boolean,
    mail: string,
    close: boolean,
    toStage2: boolean,
  ) => void;
}

const AddPersonalData: React.FC<IPersonalDataProps> = ({
  nameArg,
  surnameArg,
  peselArg,
  phoneArg,
  phoneHiddenArg,
  mailArg,
  personalDataToParent,
}) => {
  const [textPhoneValidation, setTextPhoneValidation] = useState<string>('');
  const classes = useStyles();
  const { t } = useTranslation();
  const { openAlert } = useLayoutContext();

  const [name, setName] = useState<string>(nameArg);
  const [surname, setSurname] = useState<string>(surnameArg);
  const [pesel, setPesel] = useState<string>(peselArg);
  const [validPesel, setValidPesel] = useState<boolean>(true);
  const [uniquePesel, setUniquePesel] = useState<boolean>(true);
  const [phone, setPhone] = useState<string>(phoneArg);
  const [phoneHidden, setPhoneHidden] = useState<boolean>(phoneHiddenArg);
  const [validPhone, setValidPhone] = useState<boolean>(true);
  const [mail, setMail] = useState<string>(mailArg);
  const [validMail, setValidMail] = useState<boolean>(true);
  const [uniqueMail, setUniqueMail] = useState<boolean>(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
  const [peselErrorMessage, setPeselErrorMessage] = useState<string>('');
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const multipleInvalidValidations =
    [validPesel, validPhone, uniquePesel, validMail, uniqueMail].filter((valid) => !valid).length > 1 ? true : false;
  useDocumentTitle(t('users:header.addUserButton'));

  const handleNext = () => {
    personalDataToParent(name.trim(), surname.trim(), pesel, phone, phoneHidden, mail, false, true);
  };

  const closeHandle = () => {
    personalDataToParent('', '', '', '', true, '', true, false);
  };

  const peselOnChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPesel(e.target.value.trim());
  };

  const mailOnChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMail(e.target.value.trim());
  };

  const phoneOnChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value.trim());
  };

  const validatePeselForm = () => {
    return uniquePesel && validPesel && pesel.length > 10;
  };
  const validatePhoneForm = () => {
    return validPhone && phone.length > 0;
  };
  const validateFirstAndSecondNameForm = () => {
    return name.length > 0 && surname.length > 0;
  };
  const validateEmailForm = () => {
    return validMail && uniqueMail && mail.length;
  };
  const validateForm = () => {
    return validatePeselForm() && validatePhoneForm() && validateFirstAndSecondNameForm() && validateEmailForm();
  };

  useEffect(() => {
    setNextButtonDisabled(!validateForm());
  }, [uniquePesel, uniqueMail, validPesel, validMail, validPhone, pesel, mail, phone, name, surname]);

  const validatePhoneNumber = (phone: string) => {
    const phoneNumberFormatIsValid = validatePhoneNumberInput(phone);
    const hasPolishAreaCode = validatateAreaPolishPhoneCode(phone);
    if (!phone.length && !phoneNumberFormatIsValid) {
      setValidPhone(true);
      setTextPhoneValidation('');
      return;
    }
    if (!hasPolishAreaCode) {
      setTextPhoneValidation('Kierunkowy +48 wymagany');
      setValidPhone(false);
      return;
    }
    if (!phoneNumberFormatIsValid) {
      setTextPhoneValidation('Podany numer telefonu jest nieprawidłowy');
      setValidPhone(false);
    } else {
      setTextPhoneValidation('');
      setValidPhone(true);
    }
  };

  const handlePhoneBlur = () => {
    validatePhoneNumber(phone);
  };

  const handleEmailBlur = () => {
    setValidMail(true);
    setUniqueMail(true);
    setEmailErrorMessage('');

    if (mail) {
      const validCheck = validateFormatEmailInput(mail);
      if (!validCheck) {
        setValidMail(false);
        setEmailErrorMessage('Podany e-mail jest nieprawidłowy');
      } else {
        verifyDuplicateMail(mail).then(
          (result: boolean) => {
            if (!result) {
              setUniqueMail(false);
              setEmailErrorMessage('Podany e-mail jest już wykorzystany');
            }
          },
          () => openAlert('error'),
        );
      }
    }
  };

  const handlePeselBlur = () => {
    setValidPesel(true);
    setUniquePesel(true);
    setPeselErrorMessage('');
    if (pesel) {
      const hasValidPeselFormat = isPeselValid(pesel);
      if (!hasValidPeselFormat) {
        setValidPesel(false);
        setPeselErrorMessage('Podany PESEL jest nieprawidłowy');
      } else {
        verifyDuplicatePesel(pesel).then(
          (result) => {
            if (!result) {
              setUniquePesel(false);
              setPeselErrorMessage('Podany PESEL jest już wykorzystany');
            }
          },
          () => openAlert('error'),
        );
      }
    }
  };

  return (
    <>
      <DialogTitle id="dodaj użytkownika" onClose={closeHandle}>
        <Grid container>
          <Grid item xs={6}>
            <div className={classes.modalTitle}>Dodaj użytkownika</div>
          </Grid>
          <Grid item xs={6}>
            <CloseButton onClick={closeHandle} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <AddStageInfo />
          </Grid>
          {multipleInvalidValidations ? (
            <div className={classes.errorMessage}>
              <ErrorOutlineRoundedIcon className={classes.errorIcon} />
              Formularz zawiera błędy, spróbuj wprowadzić dane ponownie
            </div>
          ) : undefined}
          <Grid item xs={12}>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={classes.formInput}
              id="imię*"
              label="Imię*"
              placeholder="Imię*"
              variant="outlined"
              aria-labelledby="imię"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              className={classes.formInput}
              id="nazwisko*"
              label="Nazwisko*"
              placeholder="Nazwisko*"
              variant="outlined"
              aria-labelledby="nazwisko"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              error={!!peselErrorMessage}
              className={classes.formInput}
              helperText={peselErrorMessage}
              value={pesel}
              onChange={peselOnChangeEvent}
              onBlur={handlePeselBlur}
              id="pesel*"
              label="PESEL*"
              placeholder="PESEL*"
              variant="outlined"
              aria-labelledby="pesel"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              error={!validPhone}
              className={classes.formInput}
              helperText={textPhoneValidation}
              style={validPhone ? undefined : { marginBottom: '47px' }}
              value={phone}
              onChange={phoneOnChangeEvent}
              onBlur={handlePhoneBlur}
              id="Numer telefonu"
              label="Numer telefonu*"
              placeholder="Numer telefonu*"
              variant="outlined"
              aria-labelledby="numer_telefonu"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <CheckboxOne
                  id="PhoneHidden"
                  name={'PhoneHidden'}
                  onChange={(e) => setPhoneHidden(e.target.checked)}
                  checked={phoneHidden}
                  value={phoneHidden}
                />
              }
              label="Ukryj numer telefonu"
              style={{ margin: '-20px 0 10px 50px' }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              error={!!emailErrorMessage}
              className={classes.formInput}
              helperText={emailErrorMessage}
              value={mail}
              onChange={mailOnChangeEvent}
              onBlur={handleEmailBlur}
              id="E-mail"
              label="E-mail*"
              placeholder="E-mail*"
              variant="outlined"
              aria-labelledby="e-mail"
              inputProps={{
                style: {
                  padding: '15.5px',
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <CancelButton onClick={closeHandle}>Anuluj</CancelButton>
          </Grid>
          <Grid item xs={6}>
            <NextButton disabled={nextButtonDisabled} onClick={handleNext}>
              Dalej
            </NextButton>
          </Grid>
        </Grid>
      </DialogTitle>
    </>
  );
};

export default AddPersonalData;
